import { reviewReplyPlaceholders } from "@swivl-mono/helpers";
import { LocationVarKey, ReviewInterface, ReviewPlatform, ReviewReplyPlatform } from "@swivl-mono/types";
import { Button, ButtonColor, ButtonWithOptions, Spinner, TextArea } from "@swivl-mono/ui";
import { DateTime } from "luxon";
import { useEffect, useState } from "react";
import { BsLightningCharge, BsLightningChargeFill } from "react-icons/bs";
import { MdAddComment, MdOutlineAddComment, MdOutlineReply } from "react-icons/md";
import { PiWarningBold } from "react-icons/pi";
import { useNavigate } from "react-router";

import { SelectableList } from "../../../../Components/SelectableList/SelectableList.Component";
import { useLocationVariables } from "../../../../Hooks/useLocationVariables";
import { AIModel } from "../../../../Models/AI/AI.Model";
import ReviewsModel from "../../../../Models/Reviews/Reviews.Model";
import { isReviewDeletedOnGoogle } from "../../Helpers/ReviewsViewHelper";
import "./Review.View.Header.scss";
import "./Review.View.Reply.scss";

const DELETED_ON_GOOGLE_MESSAGE = "This review was deleted on Google and can no longer be replied to.";

interface Props {
  review: ReviewInterface;
}

export function ReviewViewReply(props: Props) {
  const { review } = props;
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const isDeletedOnGoogle = isReviewDeletedOnGoogle(review);

  if (isEditing) {
    return <ReplyViewEdit {...props} isSaving={isSaving} setIsSaving={setIsSaving} setIsEditing={setIsEditing} />;
  }

  const deletedOnGoogleBanner = isDeletedOnGoogle ? (
    <div className="deletedOnGoogleBanner">
      <PiWarningBold />
      {DELETED_ON_GOOGLE_MESSAGE}
    </div>
  ) : null;

  if (!review.replyText) {
    return (
      <div className="ReviewViewReply">
        {deletedOnGoogleBanner}
        <div className="footer">
          {review.isArchived ? (
            <Button
              className="marginLeft"
              loading={isSaving}
              color={ButtonColor.WHITE}
              onClick={() => {
                ReviewsModel.setArchivedOnReviews([review], false);
              }}
            >
              Unarchive
            </Button>
          ) : (
            <Button
              className="marginLeft"
              loading={isSaving}
              color={ButtonColor.WHITE}
              onClick={() => {
                ReviewsModel.setArchivedOnReviews([review], true);
              }}
            >
              Archive
            </Button>
          )}
          {review.platform !== ReviewPlatform.SPOT && (
            <Button
              className="marginLeft"
              loading={isSaving}
              disabled={isDeletedOnGoogle}
              title={isDeletedOnGoogle ? DELETED_ON_GOOGLE_MESSAGE : undefined}
              onClick={() => {
                setIsEditing(true);
              }}
            >
              Reply Now
            </Button>
          )}
        </div>
      </div>
    );
  }

  const archiveOption = review.isArchived
    ? {
        title: "Unarchive",
        name: "unarchive",
        onClick: () => {
          ReviewsModel.setArchivedOnReviews([review], false);
        }
      }
    : {
        title: "Archive",
        name: "archive",
        onClick: () => {
          ReviewsModel.setArchivedOnReviews([review], true);
        }
      };

  return (
    <div className="ReviewViewReply">
      <div className="replyHeader">
        <div className="icon">
          <MdOutlineReply />
        </div>
        <div className="text">
          {review.repliedByName ? review.repliedByName + " responded on " : "Responded on "}
          {review.replyPlatfrom === ReviewReplyPlatform.SWIVL ? "swivl " : <span>{review.replyPlatfrom + " "}</span>}
          {review.replyDate ? DateTime.fromISO(review.replyDate as unknown as string).toLocaleString(DateTime.DATETIME_FULL) : undefined}
        </div>
      </div>
      <div className="replyText">{review.replyText}</div>
      {deletedOnGoogleBanner}
      <div className="footer justActions">
        <ButtonWithOptions
          isLoading={isSaving}
          color={ButtonColor.WHITE}
          buttonOptions={[
            archiveOption,
            {
              title: "Edit Reply",
              name: "edit",
              isDisabled: isDeletedOnGoogle,
              onClick: () => {
                setIsEditing(true);
              }
            },
            {
              title: "Delete Reply",
              name: "edit",
              isDisabled: isDeletedOnGoogle,
              onClick: () => {
                if (!window.confirm("Are you sure you want to delete this reply?")) return;
                setIsSaving(true);
                ReviewsModel.deleteReviewReply(review)
                  .then(() => {
                    setIsSaving(false);
                    setIsEditing(false);
                  })
                  .catch((e) => {
                    setIsSaving(false);
                    alert("There was an error deleting your reply. Please try again.");
                  });
              }
            }
          ]}
        >
          Actions
        </ButtonWithOptions>
      </div>
    </div>
  );
}

interface EditProps extends Props {
  isSaving: boolean;
  setIsSaving: (isSaving: boolean) => void;
  setIsEditing(isEditing: boolean);
}

function ReplyViewEdit(props: EditProps) {
  const { isSaving, setIsSaving, review, setIsEditing } = props;

  const [replyText, setReplyText] = useState(review.replyText);
  const [autoCompleteEnabled, setAutoCompleteEnabled] = useState(
    (!review.replyText || !review.replyText) && localStorage.getItem("reviewAutoCompleteEnabled") !== "false" ? true : false
  );
  const [isShowingQuickReplies, setIsShowingQuickReplies] = useState(false);

  const [isLoadingGeneratedReviewReply, setIsLoadingGeneratedReviewReply] = useState(autoCompleteEnabled);
  const [generatedReviewReply, setGeneratedReviewReply] = useState("");

  // Detect if reply contains variable syntax that won't be processed
  const containsVariables = replyText ? /#\*.*?\*#/.test(replyText) : false;
  // ENG-3833: template text the model left ("[Your Name]") must never reach Google.
  const placeholders = reviewReplyPlaceholders(replyText);
  const containsPlaceholders = placeholders.length > 0;

  const navigate = useNavigate();
  const variables = useLocationVariables(
    [
      LocationVarKey.ReviewReplyGeneral,
      LocationVarKey.ReviewReply5Star,
      LocationVarKey.ReviewReplyPromoter,
      LocationVarKey.ReviewReplyPassive,
      LocationVarKey.ReviewReplyDetractor,
      LocationVarKey.ReviewReply1Star
    ],
    undefined,
    undefined
  );

  useEffect(() => {
    if (autoCompleteEnabled) {
      if (!generatedReviewReply.length) {
        setIsLoadingGeneratedReviewReply(true);
        AIModel.generateReviewReply(review.text, review.rating, review.reviewerName, review.reviewerIsAnonymous, review.locationId).then(
          (generatedReviewReply) => {
            setGeneratedReviewReply(generatedReviewReply);
            setReplyText(generatedReviewReply);
            setIsLoadingGeneratedReviewReply(false);
          }
        );
      } else {
        setReplyText(generatedReviewReply);
      }
    }
  }, [autoCompleteEnabled]);

  const toggleAutoCompleteEnabled = () => {
    setAutoCompleteEnabled(!autoCompleteEnabled);
    localStorage.setItem("reviewAutoCompleteEnabled", autoCompleteEnabled ? "false" : "true");
  };

  return (
    <div className="ReviewViewReply">
      <div className="editReplyText">
        <label>You are responding publicly</label>
        {isLoadingGeneratedReviewReply ? (
          <div className="loadingGeneratedReviewReply">
            <Spinner />
          </div>
        ) : (
          <>
            <TextArea
              placeholder="Add your reply here..."
              rows={4}
              value={replyText}
              onChange={(e: any) => {
                setReplyText(e.target.value);
              }}
              className={containsVariables || containsPlaceholders ? "has-variables" : ""}
            />
            {containsVariables && (
              <div className="variable-warning">
                ⚠️ Variables like #*Location:Name*# are not supported in review replies and will appear as literal text.
              </div>
            )}
            {containsPlaceholders && (
              <div className="variable-warning">
                ⚠️ Remove the placeholder text {placeholders.join(", ")} before publishing — it would post as written.
              </div>
            )}
          </>
        )}
      </div>
      <div className="footer justButtons">
        <div className="actions">
          <div
            className={isShowingQuickReplies ? "quickReplyButton open" : "quickReplyButton"}
            onClick={() => {
              setIsShowingQuickReplies(!isShowingQuickReplies);
            }}
          >
            {isShowingQuickReplies ? <MdAddComment /> : <MdOutlineAddComment />}
          </div>
          <div className={autoCompleteEnabled ? "autoCompleteButton enabled" : "autoCompleteButton"} onClick={toggleAutoCompleteEnabled}>
            {autoCompleteEnabled ? <BsLightningChargeFill /> : <BsLightningCharge />}
          </div>
        </div>
        <Button
          loading={isSaving}
          color={ButtonColor.BLACK}
          onClick={() => {
            setIsEditing(false);
            if (review.replyText) {
              setReplyText(review.replyText);
            }
          }}
        >
          Discard
        </Button>
        <Button
          loading={isSaving}
          color={ButtonColor.ORANGE}
          disabled={containsVariables || containsPlaceholders || !replyText?.trim()}
          onClick={() => {
            if (containsVariables) {
              alert("Please remove variable syntax (#*...*#) from your reply before publishing.");
              return;
            }
            if (containsPlaceholders) {
              alert(`Please remove the placeholder text (${placeholders.join(", ")}) from your reply before publishing.`);
              return;
            }
            setIsSaving(true);
            ReviewsModel.replyToReview(review, replyText || "")
              .then((review) => {
                setIsSaving(false);
                setIsEditing(false);
              })
              .catch((e) => {
                setIsSaving(false);
                const errMsg: string = e?.message ?? "";
                if (errMsg.toLowerCase().includes("not found in google")) {
                  // The nightly full sync now detects and flags deletions proactively (see reconcileDeletedGoogleReviews) -
                  // this is now just a stopgap for the window between that sync and a review being deleted on Google.
                  alert(DELETED_ON_GOOGLE_MESSAGE);
                  if (!review.isArchived) {
                    ReviewsModel.setArchivedOnReviews([review], true);
                  }
                  return;
                }
                alert("There was an error saving your reply. Please try again.");
              });
            setIsEditing(false);
          }}
          title={containsVariables ? "Variables are not supported in review replies" : ""}
        >
          Publish Reply
        </Button>
      </div>
      <div className={isShowingQuickReplies ? "quickRepliesSpacer show" : "quickRepliesSpacer hide"} />

      {isShowingQuickReplies && (
        <SelectableList
          open={isShowingQuickReplies}
          title="Quick Replies"
          actionButtonText="Manage"
          actionButtonOnClick={() => {
            navigate("/my-storage/ReviewReply");
          }}
          openHeight={130}
          items={
            variables
              ? ([
                  { title: "General Reply", value: variables[LocationVarKey.ReviewReplyGeneral] },

                  { title: "5 Star Review", value: variables[LocationVarKey.ReviewReply5Star] },

                  { title: "Promoter Review", value: variables[LocationVarKey.ReviewReplyPromoter] },

                  { title: "Passive Review", value: variables[LocationVarKey.ReviewReplyPassive] },
                  { title: "Detractor Review", value: variables[LocationVarKey.ReviewReplyDetractor] },

                  { title: "1 Star Review", value: variables[LocationVarKey.ReviewReply1Star] }
                ] as any)
              : []
          }
          onItemSelected={(value) => {
            setReplyText(value);
            setIsShowingQuickReplies(false);
          }}
          //                 Thank you!
        />
      )}
    </div>
  );
}
